import * as THREE from "three";
import * as CANNON from "cannon-es";
import { getMap } from "./maps";

const vec3 = (arr, fallback = [0, 0, 0]) =>
  new THREE.Vector3(arr?.[0] ?? fallback[0], arr?.[1] ?? fallback[1], arr?.[2] ?? fallback[2]);

const setupScene = ({ mapName = "zen-garden" } = {}) => {
  const map = getMap(mapName);
  const atmo = map.atmosphere || {};
  const mats = map.materials || {};
  const arena = map.arena || {};

  const arenaHalf = arena.halfExtent ?? 30;
  const wallHeight = arena.wallHeight ?? 3.2;
  const wallThickness = arena.wallThickness ?? 1.6;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(atmo.sky ?? "#151529");
  const fog = atmo.fog || {};
  scene.fog = new THREE.Fog(new THREE.Color(atmo.sky ?? "#151529"), fog.near ?? 30, fog.far ?? 110);

  const camera = new THREE.PerspectiveCamera(74, 1, 0.1, 500);
  scene.add(camera);

  const world = new CANNON.World({ gravity: new CANNON.Vec3(0, -18, 0) });
  world.broadphase = new CANNON.SAPBroadphase(world);

  const groundMaterial = new CANNON.Material("ground");
  const playerMaterial = new CANNON.Material("player");
  world.addContactMaterial(
    new CANNON.ContactMaterial(groundMaterial, playerMaterial, {
      friction: 0,
      restitution: 0,
    })
  );

  const geometries = [];
  const materials = [];
  const bodies = [];
  const collidables = [];

  const makeGeo = (GeoClass, ...args) => {
    const geo = new GeoClass(...args);
    geometries.push(geo);
    return geo;
  };

  const makeMat = (params) => {
    const mat = new THREE.MeshStandardMaterial(params);
    materials.push(mat);
    return mat;
  };

  const materialCache = new Map();
  const resolveMaterial = (key) => {
    if (!key) return null;
    if (materialCache.has(key)) return materialCache.get(key);
    const def = mats[key];
    let mat = null;
    if (typeof def === "string") {
      mat = makeMat({ color: def, roughness: 0.9 });
    } else if (def && typeof def === "object") {
      mat = makeMat({
        color: def.color ?? "#888888",
        roughness: def.roughness ?? 0.9,
        metalness: def.metalness ?? 0,
        flatShading: def.flatShading ?? false,
        emissive: def.emissive,
        emissiveIntensity: def.emissiveIntensity,
      });
    } else {
      console.warn(`Material "${key}" no definido en el mapa "${map.id}"`);
    }
    materialCache.set(key, mat);
    return mat;
  };

  const addStaticBox = ({ size, position, rotationY = 0 }) => {
    const body = new CANNON.Body({
      mass: 0,
      material: groundMaterial,
      shape: new CANNON.Box(new CANNON.Vec3(size.x / 2, size.y / 2, size.z / 2)),
      position: new CANNON.Vec3(position.x, position.y, position.z),
    });
    body.quaternion.setFromEuler(0, rotationY, 0);
    world.addBody(body);
    bodies.push(body);
    return body;
  };

  const addSolidMesh = (mesh, { shadow = true } = {}) => {
    mesh.castShadow = shadow;
    mesh.receiveShadow = true;
    scene.add(mesh);
    collidables.push(mesh);
    return mesh;
  };

  const addSolidBox = ({ size, position, rotationY = 0, material }) => {
    addStaticBox({ size, position, rotationY });
    const mesh = new THREE.Mesh(
      makeGeo(THREE.BoxGeometry, size.x, size.y, size.z),
      resolveMaterial(material)
    );
    mesh.position.set(position.x, position.y, position.z);
    mesh.rotation.y = rotationY;
    return addSolidMesh(mesh);
  };

  const hemiDef = atmo.hemisphereLight || {};
  scene.add(
    new THREE.HemisphereLight(
      hemiDef.sky ?? "#35406b",
      hemiDef.ground ?? "#10141f",
      hemiDef.intensity ?? 0.6
    )
  );

  const moonDef = atmo.moon || {};
  const moon = new THREE.DirectionalLight(moonDef.color ?? "#bdd2ff", moonDef.intensity ?? 0.9);
  moon.position.set(...(moonDef.position ?? [-26, 40, -16]));
  moon.castShadow = true;
  const shadowSize = moonDef.shadowMapSize ?? 2048;
  moon.shadow.mapSize.set(shadowSize, shadowSize);
  const shadowBound = arenaHalf + (moonDef.shadowMargin ?? 12);
  moon.shadow.camera.left = -shadowBound;
  moon.shadow.camera.right = shadowBound;
  moon.shadow.camera.top = shadowBound;
  moon.shadow.camera.bottom = -shadowBound;
  moon.shadow.camera.near = 5;
  moon.shadow.camera.far = 120;
  moon.shadow.bias = moonDef.shadowBias ?? -0.0004;
  scene.add(moon);

  const starsDef = atmo.stars || {};
  const starCount = starsDef.count ?? 700;
  const starPositions = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount; i += 1) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 0.85 + 0.05);
    starPositions[i * 3] = Math.sin(phi) * Math.cos(theta) * 340;
    starPositions[i * 3 + 1] = Math.abs(Math.cos(phi)) * 340 + 10;
    starPositions[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * 340;
  }
  const starGeo = makeGeo(THREE.BufferGeometry);
  starGeo.setAttribute("position", new THREE.BufferAttribute(starPositions, 3));
  const starMat = makeMat({
    color: starsDef.color ?? "#dfe8ff",
    size: starsDef.size ?? 1.6,
    sizeAttenuation: false,
    fog: false,
  });
  scene.add(new THREE.Points(starGeo, starMat));

  addSolidMesh(
    new THREE.Mesh(
      makeGeo(THREE.CircleGeometry, arena.floorRadius ?? arenaHalf + 4, arena.floorSegments ?? 56).rotateX(-Math.PI / 2),
      resolveMaterial("ground")
    ),
    { shadow: false }
  );

  const groundBody = new CANNON.Body({
    mass: 0,
    material: groundMaterial,
    shape: new CANNON.Plane(),
  });
  groundBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
  world.addBody(groundBody);
  bodies.push(groundBody);

  const wallLength = arenaHalf * 2 + wallThickness;
  const walls = [
    { size: { x: wallLength, y: wallHeight, z: wallThickness }, position: { x: 0, y: wallHeight / 2, z: -arenaHalf } },
    { size: { x: wallLength, y: wallHeight, z: wallThickness }, position: { x: 0, y: wallHeight / 2, z: arenaHalf } },
    { size: { x: wallThickness, y: wallHeight, z: wallLength }, position: { x: -arenaHalf, y: wallHeight / 2, z: 0 } },
    { size: { x: wallThickness, y: wallHeight, z: wallLength }, position: { x: arenaHalf, y: wallHeight / 2, z: 0 } },
  ];
  walls.forEach(({ size, position }) => {
    addSolidBox({ size, position, material: "hedge" });
  });

  const postSize = arena.cornerPost?.size ?? [2.2, 3.8, 2.2];
  [
    [-arenaHalf, -arenaHalf],
    [arenaHalf, -arenaHalf],
    [-arenaHalf, arenaHalf],
    [arenaHalf, arenaHalf],
  ].forEach(([x, z]) => {
    addSolidBox({
      size: { x: postSize[0], y: postSize[1], z: postSize[2] },
      position: { x, y: postSize[1] / 2, z },
      material: "stoneDark",
    });
  });

  const buildEntity = (entity) => {
    switch (entity.type) {
      case "box": {
        const [sx, sy, sz] = entity.size ?? [1, 1, 1];
        const p = entity.position ?? [0, sy / 2, 0];
        addSolidBox({
          size: { x: sx, y: sy, z: sz },
          position: { x: p[0], y: p[1], z: p[2] },
          rotationY: entity.rotationY ?? 0,
          material: entity.material,
        });
        break;
      }

      case "platform": {
        const p = entity.position ?? [0, entity.height ? entity.height / 2 : 0.3, 0];
        const rTop = entity.radiusTop ?? 6;
        const rBottom = entity.radiusBottom ?? rTop;
        const height = entity.height ?? 0.6;
        const mesh = addSolidMesh(
          new THREE.Mesh(
            makeGeo(THREE.CylinderGeometry, rTop, rBottom, height, entity.segments ?? 36),
            resolveMaterial(entity.material)
          ),
          { shadow: false }
        );
        mesh.position.set(p[0], p[1], p[2]);
        const body = new CANNON.Body({
          mass: 0,
          material: groundMaterial,
          shape: new CANNON.Cylinder(rTop, rBottom, height, 20),
          position: new CANNON.Vec3(p[0], p[1], p[2]),
        });
        world.addBody(body);
        bodies.push(body);
        break;
      }

      case "column": {
        const p = entity.position ?? [0, (entity.height ?? 6) / 2, 0];
        const radius = entity.radius ?? 0.2;
        const height = entity.height ?? 6;
        const segments = entity.segments ?? 10;
        const mesh = addSolidMesh(
          new THREE.Mesh(
            makeGeo(THREE.CylinderGeometry, radius * 0.85, radius, height, segments),
            resolveMaterial(entity.material)
          )
        );
        mesh.position.set(p[0], p[1], p[2]);
        const body = new CANNON.Body({
          mass: 0,
          material: groundMaterial,
          shape: new CANNON.Cylinder(radius, radius, height, 10),
          position: new CANNON.Vec3(p[0], p[1], p[2]),
        });
        world.addBody(body);
        bodies.push(body);
        break;
      }

      case "rock": {
        const scale = entity.scale ?? [1, 1, 1];
        const p = entity.position ?? [0, scale[1] * 0.4, 0];
        const mesh = addSolidMesh(
          new THREE.Mesh(makeGeo(THREE.DodecahedronGeometry, 1.1, 0), resolveMaterial(entity.material))
        );
        mesh.position.set(p[0], p[1], p[2]);
        mesh.scale.set(...scale);
        mesh.rotation.set(entity.rotationX ?? 0.12, entity.rotationY ?? 0, entity.rotationZ ?? -0.08);
        addStaticBox({
          size: { x: scale[0] * 1.9, y: scale[1] * 1.6, z: scale[2] * 1.9 },
          position: { x: p[0], y: scale[1] * 0.8, z: p[2] },
          rotationY: entity.rotationY ?? 0,
        });
        break;
      }

      case "lantern": {
        const x = entity.position?.[0] ?? 0;
        const z = entity.position?.[2] ?? 0;
        const baseW = entity.baseWidth ?? 0.9;
        const baseH = entity.baseHeight ?? 1.5;
        addStaticBox({
          size: { x: baseW, y: baseH, z: baseW },
          position: { x, y: baseH / 2, z },
        });
        const baseMat = resolveMaterial("stoneDark");
        const baseMesh = new THREE.Mesh(makeGeo(THREE.BoxGeometry, baseW, baseH, baseW), baseMat);
        baseMesh.position.set(x, baseH / 2, z);
        addSolidMesh(baseMesh);
        const windowMat = makeMat({
          color: mats.lanternWindow?.color ?? "#66513a",
          roughness: mats.lanternWindow?.roughness ?? 0.6,
          emissive: typeof mats.lanternGlow === "string" ? mats.lanternGlow : "#ffb26b",
          emissiveIntensity: entity.glowIntensity ?? 1.4,
        });
        const lightBox = new THREE.Mesh(
          makeGeo(THREE.BoxGeometry, baseW * 0.94, baseW * 0.94, baseW * 0.94),
          windowMat
        );
        lightBox.position.set(x, baseH + baseW * 0.5, z);
        addSolidMesh(lightBox);
        const cap = new THREE.Mesh(
          makeGeo(THREE.ConeGeometry, baseW * 0.89, 0.55, 4),
          baseMat
        );
        cap.position.set(x, baseH + baseW * 0.97 + 0.275, z);
        cap.rotation.y = Math.PI / 4;
        addSolidMesh(cap);
        const glow = new THREE.PointLight(
          typeof mats.lanternGlow === "string" ? mats.lanternGlow : "#ffb26b",
          entity.lightIntensity ?? 14,
          entity.lightDistance ?? 15,
          2
        );
        glow.position.set(x, baseH + baseW * 0.67, z);
        scene.add(glow);
        break;
      }

      default:
        console.warn(`Entidad desconocida "${entity.type}" en el mapa "${map.id}"`);
    }
  };

  (map.entities || []).forEach(buildEntity);

  const waypoints = (map.spawns?.waypoints ?? []).map((wp) => vec3(wp));
  const spawnPoints = (map.spawns?.players ?? [[0, 0.5, -24]]).map((sp) => vec3(sp));

  const dispose = () => {
    bodies.forEach((body) => world.removeBody(body));
    geometries.forEach((geo) => geo.dispose());
    materials.forEach((mat) => mat.dispose());
    scene.clear();
  };

  return {
    scene,
    camera,
    world,
    groundMaterial,
    playerMaterial,
    collidables,
    waypoints,
    spawnPoints,
    exposure: atmo.exposure ?? 1.1,
    dispose,
    mapInfo: { mapName: map.id, displayName: map.displayName ?? map.id, arenaHalf },
  };
};

export default setupScene;
